import { ProseDensityProvider } from '@heidi/answer-kit';
import { Button } from '@heidi/design-system/button';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@heidi/design-system/sheet';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { Cloud, Loader2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ChatItem } from '../../../contracts';
import type { LiveDelegateStep } from '../../../data';
import { computeLatestTodoPlan, PLAN_COLLAPSE_DISABLED } from '../../../data';
import { PlanStepsPanel } from '../timeline/agent-todo/PlanStepsPanel';

/** The cloud resident's machine as the drawer shows it. */
export interface ResidentMachine {
  displayName: string | null;
  /** The provider's state, classified app-side; anything not running or paused is starting. */
  state: 'running' | 'paused' | 'starting';
}

/**
 * The app's half of the drawer: the selector verdict, the machine query and the
 * live screen. Pass one module-level object, so each hook keeps its identity.
 */
export interface YourResidentDrawerHost {
  /** The desktop-target selector verdict the resident ships behind. A hook. */
  useIsEnabled: () => boolean;
  /** The resident's machine, polled while `enabled`; undefined until it loads. A hook. */
  useResidentMachine: (enabled: boolean) => ResidentMachine | undefined;
  /** The resident's live screen. */
  renderLiveView: () => ReactNode;
}

/**
 * "Your Resident" — a header pill over the chat that opens a right-side
 * drawer with the cloud resident's live screen, the run's current to-dos,
 * and the live reasoning step. The always-on peek surface for the cloud VM
 * (replaces the old floating bottom-right live dock).
 */
export const YourResidentDrawer = ({
  items,
  liveDelegateStep,
  host,
}: {
  items: ChatItem[];
  liveDelegateStep?: LiveDelegateStep;
  host: YourResidentDrawerHost;
}): JSX.Element | null => {
  const intl = useIntl();
  const enabled = host.useIsEnabled();
  const [open, setOpen] = useState(false);

  const machine = host.useResidentMachine(enabled);

  const plan = useMemo(() => computeLatestTodoPlan(items), [items]);

  if (!enabled || !machine) return null;

  const name =
    machine.displayName?.trim() ||
    intl.formatMessage({ id: 'GVyViy2sfL', defaultMessage: 'Your Resident' });
  const isLive = machine.state === 'running';
  const isPaused = machine.state === 'paused';
  const liveLabel = liveDelegateStep?.label?.trim();

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        className="absolute right-4 top-3 z-20 gap-1.5 rounded-full shadow-sm"
        data-testid="your-resident-drawer-trigger"
      >
        <Cloud className="size-3.5" />
        {name}
        {isLive && <span className="size-1.5 animate-pulse rounded-full bg-green-500" />}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex w-[26rem] max-w-full flex-col gap-0 p-0"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <SheetTitle className="flex items-center gap-2 text-sm font-semibold">
              <Cloud className="size-4" />
              {name}
            </SheetTitle>
            {isLive ? (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-green-600">
                <span className="size-1.5 animate-pulse rounded-full bg-green-500" />
                {intl.formatMessage({ id: 'Dn82ALx/+V', defaultMessage: 'Live' })}
              </span>
            ) : isPaused ? (
              <TypographyCaption className="text-text-tertiary">
                {intl.formatMessage({ id: 'C2iTEHtK//', defaultMessage: 'Paused' })}
              </TypographyCaption>
            ) : (
              <TypographyCaption className="text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: 'Starting…',
                  description: 'Your Resident drawer badge while Daytona is waking',
                  id: 'vHivR3oh15',
                })}
              </TypographyCaption>
            )}
          </div>

          <div className="flex flex-col gap-1 px-4 pt-4">
            <div className="overflow-hidden rounded-lg border border-border bg-black">
              {isPaused ? (
                <div className="flex aspect-video w-full items-center justify-center text-xs text-white/60">
                  {intl.formatMessage({
                    id: 'Nnj5V3oF5P',
                    defaultMessage: 'Your resident is paused.',
                  })}
                </div>
              ) : (
                host.renderLiveView()
              )}
            </div>
            <TypographyCaption className="text-center text-text-tertiary">
              {intl.formatMessage(
                { id: 'HC3YXRrmF1', defaultMessage: "{name}'s screen" },
                { name }
              )}
            </TypographyCaption>
          </div>

          <ScrollArea className="min-h-0 flex-1 px-4 pb-4 pt-3">
            <div className="flex flex-col gap-4">
              {liveLabel && (
                <div className="flex flex-col gap-1.5">
                  <TypographyCaptionBold className="text-text-secondary">
                    {intl.formatMessage({ id: 'tgvES0v2HS', defaultMessage: 'Now' })}
                  </TypographyCaptionBold>
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-background-tertiary/60 p-2.5">
                    <Loader2 className="size-3.5 shrink-0 animate-spin text-text-secondary" />
                    <span className="min-w-0 break-words text-xs text-text-secondary">
                      {liveLabel}
                    </span>
                    {liveDelegateStep && liveDelegateStep.count > 0 && (
                      <span className="ms-auto shrink-0 text-[10px] tabular-nums text-text-tertiary">
                        {intl.formatMessage(
                          { id: 'DNMRsv+xpx', defaultMessage: 'Step {count}' },
                          { count: liveDelegateStep.count }
                        )}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <TypographyCaptionBold className="text-text-secondary">
                  {intl.formatMessage({ id: 'f55DWz3lnX', defaultMessage: 'To-dos' })}
                </TypographyCaptionBold>
                {plan && plan.state.items.length > 0 ? (
                  // A side panel, not the thread, even though it mounts inside one.
                  <ProseDensityProvider density="compact">
                    <PlanStepsPanel
                      items={plan.state.items}
                      collapseAfter={PLAN_COLLAPSE_DISABLED}
                    />
                  </ProseDensityProvider>
                ) : (
                  <TypographyCaption className="text-text-tertiary">
                    {intl.formatMessage({
                      id: 'J/5KWYJ0Yv',
                      defaultMessage: 'No plan yet — to-dos appear when a task starts.',
                    })}
                  </TypographyCaption>
                )}
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </>
  );
};
